import { useTheme } from '@emotion/react';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';
import type { VersionSelect as VersionSelectType } from '~/ui/domain/version-select/VersionSelect';
import { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import { useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { ContentSections } from '../components/Common';
import { Title } from '../components/SidepanelTitle';
import { DescriptionSectionSkeleton, TitleSkeleton } from '../components/SkeletonLoaders';
import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { useDraftJobItemDetails } from '../job-profile-section/use-draft-job-item-details';
import { ActiveLanguageProvider } from '../providers/ActiveLanguageProvider';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { CompareVersionHeader } from './CompareVersionHeader';
import { DescriptionComparisonSection } from './DescriptionComparisonSection';
import { SkillsComparisonSection } from './SkillsComparisonSection';

const useCompareVersionData = (
    architectureId: string,
    itemId: string,
    itemType: 'job-level' | 'job-role',
    versionA: string,
    versionB: string,
): {
    readonly formattedVersions: readonly VersionSelectType[];
    readonly leftProfile: JobProfileComponent | undefined;
    readonly rightProfile: JobProfileComponent | undefined;
    readonly tmpVersion: VersionSelectType | null;
} => {
    const { draftData } = useDraftJobItemDetails({
        itemId,
    });

    const { data: left } = useJobItemDetails({
        architectureId,
        itemId,
        itemType,
        version: versionA,
    });

    const { data: right } = useJobItemDetails({
        architectureId,
        itemId,
        itemType,
        version: versionB,
    });

    const { data: versions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const { formattedVersions, newTmpVersion } = useFormattedVersions(versions ?? []);

    if (draftData !== undefined) {
        return {
            formattedVersions,
            leftProfile: left?.jobProfile,
            rightProfile:
                versionB === draftData?.versionInfo?.version?.toString() ? draftData?.jobProfile : right?.jobProfile,
            tmpVersion: newTmpVersion,
        };
    }

    return {
        formattedVersions,
        leftProfile: left?.jobProfile,
        rightProfile: right?.jobProfile,
        tmpVersion: null,
    };
};

const CompareVersionLoadingState: FunctionComponent = () => {
    const theme = useTheme();

    return (
        <Tile data-testid="loading" style={{ position: 'relative', zIndex: 1 }}>
            <ScrollArea>
                <TileContent>
                    <div style={{ marginTop: theme.spacing['4xl'] }}>
                        <TitleSkeleton />
                    </div>
                    <div
                        style={{
                            display: 'grid',
                            gap: theme.spacing.m,
                            gridTemplateColumns: '1fr 1fr',
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                background: theme.colors.gray[200],
                                borderRadius: theme.borderRadius.m,
                                height: theme.spacing['6xl'],
                            }}
                        />
                        <div
                            style={{
                                background: theme.colors.gray[200],
                                borderRadius: theme.borderRadius.m,
                                height: theme.spacing['6xl'],
                            }}
                        />
                    </div>
                    <ContentSections style={{ gridTemplateColumns: '1fr' }}>
                        <div
                            style={{
                                display: 'grid',
                                gap: theme.spacing.m,
                                gridTemplateColumns: '1fr 1fr',
                            }}
                        >
                            <div>
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                            </div>
                            <div>
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                                <DescriptionSectionSkeleton />
                            </div>
                        </div>
                    </ContentSections>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

const VersionSelectorsGrid: FunctionComponent<{
    readonly formattedVersion: readonly VersionSelectType[];
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly placeholder: string;
    readonly tmpVersion: VersionSelectType | null;
    readonly versionA: string;
    readonly versionB: string;
}> = ({ formattedVersion, itemId, itemType, placeholder, tmpVersion, versionA, versionB }) => {
    const { openCompareTile } = useJobArchitectureUrlTileActions();
    const versionsWithTmp = useMemo(
        () => [tmpVersion, ...formattedVersion].filter((version): version is VersionSelectType => version !== null),
        [tmpVersion, formattedVersion],
    );

    useEffect(() => {
        if (versionsWithTmp.find((version) => version.id === versionB) === undefined) {
            openCompareTile({
                profileId: itemId,
                type: itemType,
                versionA,
                versionB: versionA,
            });
        }
    }, [versionsWithTmp, itemId, itemType, versionA, versionB, openCompareTile]);

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%' }}>
            <div>
                <VersionSelect
                    currentVersion={formattedVersion.find((version) => version.id === versionA)}
                    disabled={formattedVersion.length === 0}
                    placeholder={placeholder}
                    variant="secondary"
                    versions={formattedVersion}
                    onSwitchVersion={(id: string): void => {
                        openCompareTile({
                            profileId: itemId,
                            type: itemType,
                            versionA: id ?? '',
                            versionB,
                        });
                    }}
                />
            </div>
            <div>
                <VersionSelect
                    currentVersion={versionsWithTmp.find((version) => version.id === versionB)}
                    disabled={formattedVersion.length === 0}
                    placeholder={placeholder}
                    variant="secondary"
                    versions={versionsWithTmp}
                    onSwitchVersion={(id: string): void => {
                        openCompareTile({
                            profileId: itemId,
                            type: itemType,
                            versionA,
                            versionB: id,
                        });
                    }}
                />
            </div>
        </div>
    );
};

const CompareVersionContent: FunctionComponent<{
    readonly formattedVersion: readonly VersionSelectType[];
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly leftProfile: JobProfileComponent;
    readonly onClose: () => void;
    readonly placeholder: string;
    readonly rightProfile: JobProfileComponent;
    readonly title: string;
    readonly tmpVersion: VersionSelectType | null;
    readonly versionA: string;
    readonly versionB: string;
}> = ({
    formattedVersion,
    itemId,
    itemType,
    leftProfile,
    onClose,
    placeholder,
    rightProfile,
    title,
    tmpVersion,
    versionA,
    versionB,
}) => {
    const theme = useTheme();

    return (
        <Tile data-testid="compare-version" style={{ position: 'relative', zIndex: 1 }}>
            <ScrollArea>
                <TileContent>
                    <CompareVersionHeader onClose={onClose} />
                    <Title style={{ marginTop: theme.spacing['4xl'] }}>{title}</Title>
                    <VersionSelectorsGrid
                        formattedVersion={formattedVersion}
                        itemId={itemId}
                        itemType={itemType}
                        placeholder={placeholder}
                        tmpVersion={tmpVersion}
                        versionA={versionA}
                        versionB={versionB}
                    />
                    <ContentSections style={{ gridTemplateColumns: '1fr' }}>
                        <DescriptionComparisonSection leftProfile={leftProfile} rightProfile={rightProfile} />
                        <SkillsComparisonSection leftProfile={leftProfile} rightProfile={rightProfile} />
                    </ContentSections>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

export const CompareVersion: FunctionComponent<{
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly onClose: () => void;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly versionA: string;
    readonly versionB: string;
}> = ({ architectureId, itemId, itemType, onClose, preferredLanguage, versionA, versionB }) => {
    const { translate } = useContext(ApplicationContext);
    const data = useCompareVersionData(architectureId, itemId, itemType, versionA, versionB);

    if (is.undefined(data.leftProfile) || is.undefined(data.rightProfile)) {
        return <CompareVersionLoadingState />;
    }

    return (
        <ActiveLanguageProvider preferredLanguage={preferredLanguage} profiles={[data.leftProfile, data.rightProfile]}>
            <CompareVersionContent
                formattedVersion={data.formattedVersions}
                itemId={itemId}
                itemType={itemType}
                leftProfile={data.leftProfile}
                placeholder={translate('pages.job-architecture-v2.job-profile-version-placeholder')}
                rightProfile={data.rightProfile}
                title={translate('pages.job-architecture-v2.job-profile-compare-versions')}
                tmpVersion={data.tmpVersion}
                versionA={versionA}
                versionB={versionB}
                onClose={onClose}
            />
        </ActiveLanguageProvider>
    );
};
